"use client"

import styled from "@emotion/styled"
import React, { useEffect, useState } from "react"
import { Check2Icon } from "../icons"
import ArrowDown2Icon from "../icons/ArrowDown2Icon"
import { MultiSelectCheckbox } from "./shared"

const StyledInlineDropdownWrapper = styled.div`
  position: relative;
`

const StyledInlineDropdownTrigger = styled.button<{ $hasValue: boolean; $isOpen: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 12px;
  border: 1px solid ${({ $isOpen }) => ($isOpen ? "#0e2846" : "#e4e4e4")};
  border-radius: 8px;
  background-color: white;
  font-family: var(--simpler-pro-full) !important;
  font-size: 16px;
  font-weight: ${({ $hasValue }) => ($hasValue ? 700 : 300)};
  color: ${({ $hasValue }) => ($hasValue ? "#0e2846" : "#687079")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:focus {
    outline: none;
  }

  svg {
    transition: transform 0.2s;
    transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0)")};
  }
`

const StyledInlineDropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin-top: 4px;
  background-color: white;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(42, 42, 42, 0.12);
  max-height: 180px;
  overflow: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledInlineDropdownItem = styled.div<{ $isSelected: boolean; $isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  gap: 12px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? "default" : "pointer")};
  border-radius: 4px;
  font-family: var(--simpler-pro-full) !important;
  font-size: 16px;
  font-weight: 300;
  color: ${({ $isDisabled, $isSelected }) =>
    $isDisabled ? "#b0b0b0" : $isSelected ? "#0000fa" : "#2a2a2a"};
  text-decoration: ${({ $isDisabled }) => ($isDisabled ? "line-through" : "none")};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? "none" : "auto")};

  &:hover {
    background-color: ${({ $isDisabled }) =>
      $isDisabled ? "transparent" : "rgba(0, 0, 250, 0.04)"};
  }
`

function InlineTimeDropdown({
  value,
  placeholder,
  options,
  disabledOptions,
  onChange,
}: {
  value: string
  placeholder: string
  options: string[]
  disabledOptions?: Set<string>
  // eslint-disable-next-line no-unused-vars
  onChange: (val: string) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [isOpen])

  return (
    <StyledInlineDropdownWrapper ref={wrapperRef}>
      <StyledInlineDropdownTrigger
        type="button"
        $hasValue={!!value}
        $isOpen={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{value || placeholder}</span>
        <ArrowDown2Icon />
      </StyledInlineDropdownTrigger>
      {isOpen && (
        <StyledInlineDropdownList>
          {options.map((opt) => {
            const isSelected = value === opt
            const isDisabled = disabledOptions?.has(opt) ?? false
            return (
              <StyledInlineDropdownItem
                key={opt}
                $isSelected={isSelected}
                $isDisabled={isDisabled}
                onClick={
                  isDisabled
                    ? undefined
                    : () => {
                        onChange(isSelected ? "" : opt)
                        setIsOpen(false)
                      }
                }
              >
                <MultiSelectCheckbox $isSelected={isSelected}>
                  {isSelected && <Check2Icon />}
                </MultiSelectCheckbox>
                {opt}
              </StyledInlineDropdownItem>
            )
          })}
        </StyledInlineDropdownList>
      )}
    </StyledInlineDropdownWrapper>
  )
}

export default InlineTimeDropdown
